import { tagged } from "@mirage/service-logging";
import { isEsvConfig } from "@mirage/shared/util/esv";
import { atom, useAtom } from "jotai";
import { useEffect } from "react";
import {
  DashEsvController,
  DashEsvControllerWithEventService,
  FenixWindow,
} from "../types";

const validateInterfaceKeys = <T>() => {
  return <K extends keyof T>(keys: K[]): K[] => keys;
};

export const dashEsvControllerKeys = validateInterfaceKeys<DashEsvController>()(
  [
    "getCurrentAccount",
    "reloadView",
    "showDashWindow",
    "hideDashWindow",
    "launchPersistentWindow",
    "getAccessToken",
    "getShortcutValue",
    "sendMessageToRenderer",
    "getFeatureSessionId",
    "getSettings",
  ],
);

const nativeApiAtom = atom<DashEsvControllerWithEventService | undefined>(
  undefined,
);

const logger = tagged("fss-native-useNativeApi");

export const useAddDashEsvEventListener = () => {
  const [nativeApi, setNativeApi] = useAtom(nativeApiAtom);

  useEffect(() => {
    // only do this if we're in an ESV
    if (!isEsvConfig()) {
      return;
    }

    // if we've already created the nativeAPI, just return it so we don't create
    // a new instance and trigger re-renders.
    if (nativeApi) {
      return;
    }

    const getFenixWindow = async (): Promise<FenixWindow | undefined> => {
      const fenixWindow = window as unknown as FenixWindow;
      if (
        fenixWindow.fenixInitializationComplete === null ||
        fenixWindow.fenixInitializationComplete === undefined
      ) {
        logger.error(
          "Fenix window platform check is not available on window object",
        );
        return undefined;
      }
      await fenixWindow.fenixInitializationComplete;

      return fenixWindow;
    };

    getFenixWindow().then((fenixWindow) => {
      // Assert that every method in DashEsvController exists on fenixWindow.controller. For now, this errors out
      // if the interface method doesn't exist, but there are a few avenues we've got for more graceful degradation.

      // 1. We could have a default implementation where applicable
      // 2. For specific feature launches, we should hide em behind a stormcrow so we're not tied exclusively to the
      //    desktop client release cycle. Then, once the feature is released, we can remove the stormcrow and error out
      //    if the method is not found.

      if (!(fenixWindow && fenixWindow.controller)) {
        logger.error("fenixWindow.controller is not available");
        return;
      }

      for (const key of dashEsvControllerKeys) {
        if (typeof fenixWindow.controller[key] !== "function") {
          // For now, we'll just log an error, but when we go out to users we should apply one of the strategies above.
          logger.error(
            `Required method ${String(key)} is missing or not a function on fenixWindow.controller`,
          );
        }
      }
      const combinedApi: DashEsvControllerWithEventService = {
        ...fenixWindow.controller,
        addDashEsvEventListener:
          fenixWindow.dashIntegrationService?.addEventListener ??
          (() => () => {}),
      };
      setNativeApi(combinedApi);
    });
  }, [nativeApi, setNativeApi]);

  return { addDashEsvEventListener: nativeApi?.addDashEsvEventListener };
};
